import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { OnlineFollowing } from './OnlineFollowing';
import { NewsletterWidget } from './NewsletterWidget';
import { getProfilePicUrl } from '../../utils/profilePic';
import { CommandPalette, useCommandPalette } from '../CommandPalette';
import {
  Home, Search, Library, Upload, LayoutDashboard, LogOut, User,
  Music, ListMusic, Radio, MessageCircle, Users, DollarSign,
  Settings, Trophy, Shield, Disc, Bot, LogIn, UserPlus, Activity,
  Crown, Ticket, Store, Play, Mic, Megaphone, MapPin, FileText,
  BarChart3, Coins, Sliders, Wallet, Share2, Sun, Moon, Clock, Waves,
  Headphones, ShoppingBag, ShoppingCart, Sparkles, Repeat, Film,
  PartyPopper, Briefcase, Heart, CreditCard, FolderOpen
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

export const Sidebar = ({ onNavigate }) => {
  const { user, logout, isArtist, isProducer, isAIArtist, token } = useAuth();
  const { mode, toggleTheme, autoMode, enableAutoTheme } = useTheme();
  const navigate = useNavigate();
  const isAuthenticated = !!token;
  const isAdmin = user?.is_admin === true;
  const isAiArtist = isAIArtist || user?.is_ai_artist === true;
  const isDJ = user?.is_dj === true;
  const isPromoter = user?.is_promoter === true;
  const isVIP = user?.is_vip === true;
  const isAdvertiser = user?.is_advertiser === true;
  const isReviewer = user?.is_reviewer === true;
  const isCreator = isArtist || isProducer || isAiArtist;
  // Creator Pro is available to any "creator" role: artist/AI artist/producer/DJ/reviewer
  // (NOT promoter/advertiser/fan-only — see backend creator_pro.py CREATOR_ROLE_FLAGS)
  const isCreatorProEligible = isCreator || isDJ || isReviewer;
  const hasCreatorPro = user?.creator_pro === true;
  const isFan = isAuthenticated && !isCreator && !isDJ && !isPromoter && !isAdmin;

  // Spin the brand logo on every sidebar menu interaction.
  // We toggle a `logoSpinTick` to force re-trigger the CSS animation
  // even when the same item is clicked twice in a row.
  const [logoSpinTick, setLogoSpinTick] = useState(0);
  const spinLogo = () => setLogoSpinTick((t) => t + 1);

  const handleLogout = () => {
    spinLogo();
    logout();
    navigate('/', { replace: true });
    if (onNavigate) onNavigate();
  };

  const handleNavClick = () => {
    spinLogo();
    if (onNavigate) onNavigate();
  };

  const handleLoginRedirect = (feature) => {
    spinLogo();
    toast.error(`Please login to access ${feature}`);
    navigate('/login');
    if (onNavigate) onNavigate();
  };

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-2.5 px-3 py-2 text-xs font-mono uppercase tracking-wider transition-all border-l-2 ${
      isActive 
        ? 'bg-[#111111] text-white border-[#00CCFF]' 
        : 'text-[#888888] border-transparent hover:text-white hover:bg-[#0a0a0a] hover:border-[#333]'
    }`;

  const getUserRole = () => {
    if (isAdmin) return { label: 'Admin', color: 'text-[#FFDA33]', icon: Shield };
    if (isAiArtist && isProducer) return { label: 'AI Artist/Producer', color: 'text-[#9b59b6]', icon: Bot };
    if (isAiArtist && isProducer) return { label: 'AI Artist/Producer', color: 'text-[#9b59b6]', icon: Bot };
    if (isArtist && isProducer) return { label: 'Artist/Producer', color: 'text-[#ff6b6b]', icon: Disc };
    if (isDJ && isPromoter) return { label: 'DJ/Promoter', color: 'text-[#00CCFF]', icon: Radio };
    if (isDJ) return { label: 'DJ', color: 'text-[#00CCFF]', icon: Radio };
    if (isPromoter) return { label: 'Promoter', color: 'text-[#ff6b6b]', icon: Megaphone };
    if (isAiArtist) return { label: 'AI Artist', color: 'text-[#9b59b6]', icon: Bot };
    if (isProducer) return { label: 'Producer', color: 'text-[#ff6b6b]', icon: Disc };
    if (isArtist) return { label: 'Artist', color: 'text-[#00CCFF]', icon: Music };
    return { label: 'Fan', color: 'text-[#888888]', icon: User };
  };

  const userRole = getUserRole();
  const RoleIcon = userRole.icon;

  // Global ⌘K / Ctrl+K command palette.
  const palette = useCommandPalette();

  return (
    <aside 
      data-testid="sidebar"
      className="sidebar-nav w-48 h-full bg-[#000000] border-r border-[#1a1a1a] flex flex-col"
    >
      <CommandPalette open={palette.open} onOpenChange={palette.setOpen} />
      {/* Logo — new circular brand mark with full wordmark.
          Spins once on every menu interaction. `key` re-mounts the <img>
          so the animation re-fires even when triggered repeatedly. */}
      <div className="p-3 border-b border-[#1a1a1a]">
        <a
          href="/home"
          className="block cursor-pointer flex items-center justify-center"
          data-testid="sidebar-logo-link"
          onClick={spinLogo}
        >
          <div className="bg-black rounded-2xl p-1.5 shadow-[0_0_24px_rgba(255,218,51,0.25)] hover:scale-105 transition-transform">
            <img
              key={logoSpinTick}
              src="/onevoice-logo-circular.png"
              alt="One Voice Distribution"
              className={`w-full h-auto max-h-28 sm:max-h-32 object-contain ${logoSpinTick > 0 ? 'sidebar-logo-spin' : ''}`}
              data-testid="sidebar-logo"
            />
          </div>
        </a>
      </div>

      {/* Command palette trigger — clickable surface that mirrors the
          ⌘K / Ctrl+K keyboard shortcut. Sits directly under the logo so
          it's the first thing users see and a clear escape-hatch from
          the long sidebar list. */}
      <button
        type="button"
        onClick={() => palette.setOpen(true)}
        className="mx-3 mt-2 mb-1 flex items-center gap-2 px-3 py-2 bg-[#0a0a0a] border border-[#1a1a1a] hover:border-[#333] hover:bg-[#111] transition-colors group"
        data-testid="command-palette-trigger"
        aria-label="Open command palette (Ctrl+K / Cmd+K)"
      >
        <Search size={14} className="text-[#666] group-hover:text-[#00CCFF] transition-colors" />
        <span className="flex-1 text-left text-[11px] font-mono text-[#666] uppercase tracking-wider">
          Search…
        </span>
        <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-black border border-[#222] text-[#666] text-[10px] font-mono">
          ⌘K
        </kbd>
      </button>

      {/* Navigation */}
      <nav className="flex-1 py-2 overflow-y-auto">
        {/* ===== PUBLIC MENU — Accessible by everyone (visitors + logged in) ===== */}
        <div className="mb-4">
          <p className="px-4 text-[10px] text-[#888888] uppercase tracking-widest mb-2">Menu</p>
          
          {isAuthenticated ? (
            <NavLink to="/home" className={navLinkClass} data-testid="nav-home" onClick={handleNavClick}>
              <Home size={16} className="text-[#00CCFF]" /> Home
            </NavLink>
          ) : (
            <NavLink to="/" className={navLinkClass} data-testid="nav-home" onClick={handleNavClick}>
              <Home size={16} className="text-[#00CCFF]" /> Home
            </NavLink>
          )}

          {/* iter_407 — My Library is now in the main menu for every
              signed-in user (was orphaned at the bottom of the sidebar).
              Any user can buy content from another user, so the library
              is universally relevant — not creator-only. */}
          {isAuthenticated && (
            <NavLink to="/library" className={navLinkClass} data-testid="nav-library" onClick={handleNavClick}>
              <Library size={16} className="text-[#74b9ff]" /> My Library
            </NavLink>
          )}

          <NavLink to="/browse" className={navLinkClass} data-testid="nav-browse" onClick={handleNavClick}>
            <Search size={16} className="text-[#ff6b6b]" /> Browse
          </NavLink>
          
          <NavLink to="/discover/artists" className={navLinkClass} data-testid="nav-discover-artists" onClick={handleNavClick}>
            <Music size={16} className="text-[#ff9f43]" /> Discover Artists
          </NavLink>
          
          <NavLink to="/discover/producers" className={navLinkClass} data-testid="nav-discover-producers" onClick={handleNavClick}>
            <Disc size={16} className="text-[#a29bfe]" /> Discover Producers
          </NavLink>
          
          <NavLink to="/ai-discover" className={navLinkClass} data-testid="nav-ai-discover" onClick={handleNavClick}>
            <Bot size={16} className="text-[#9b59b6]" /> Discover AI Artists
          </NavLink>

          <NavLink to="/ai-detector" className={navLinkClass} data-testid="nav-ai-detector" onClick={handleNavClick}>
            <Waves size={16} className="text-[#00CCFF]" /> AI Detector
          </NavLink>
          
          <NavLink to="/leaderboard" className={navLinkClass} data-testid="nav-leaderboard" onClick={handleNavClick}>
            <Trophy size={16} className="text-[#FFDA33]" /> Leaderboard
          </NavLink>

          {/* One Voice Radio — unified live radio with All Genre / Music /
              AI / Beats tabs. Replaces the old separate Free Radio + AI
              Radio sidebar entries. */}
          <NavLink to="/radio" className={navLinkClass} data-testid="nav-radio" onClick={handleNavClick}>
            <Radio size={16} className="text-[#FFDA33]" /> One Voice Radio
          </NavLink>

          {/* One Voice Studio — public link. Visitors land on the marketing
              preview, signed-in users get the full DAW + Beat Maker. */}
          <NavLink to="/studio" className={navLinkClass} data-testid="nav-studio-public" onClick={handleNavClick}>
            <Disc size={16} className="text-[#FFDA33]" /> One Voice Studio
          </NavLink>

          {/* Studio Sessions — the `.ovs` project list (Pro-Tools-style
              session browser). Artist-only — guests don't see this. */}
          {isArtist && (
            <NavLink to="/studio/sessions" className={navLinkClass} data-testid="nav-studio-sessions" onClick={handleNavClick}>
              <FolderOpen size={16} className="text-[#00CCFF]" /> Studio Sessions
              <span className="ml-auto text-[9px] uppercase tracking-wider bg-[#00CCFF]/15 text-[#00CCFF] border border-[#00CCFF]/40 rounded-full px-1.5 py-0.5 font-mono">NEW</span>
            </NavLink>
          )}

          {/* iter_461 — AI Studio hub: A&R Scout, Cover Art, Songwriter.
              Sparkles icon + amber/pink gradient makes it stand out.
              Visible to everyone (signed-in users can save / get quota,
              non-signed-in see a teaser landing). */}
          <NavLink to="/ai-studio" className={navLinkClass} data-testid="nav-ai-studio" onClick={handleNavClick}>
            <Sparkles size={16} className="text-[#ff6b9d]" />
            <span className="bg-gradient-to-r from-[#ff6b9d] to-[#FFDA33] bg-clip-text text-transparent font-semibold">
              AI Studio
            </span>
            <span className="ml-auto text-[9px] uppercase tracking-wider bg-[#ff6b9d]/15 text-[#ff6b9d] border border-[#ff6b9d]/40 rounded-full px-1.5 py-0.5 font-mono">NEW</span>
          </NavLink>

          {/* Producer Sample-Pack Marketplace — community-uploaded sounds. */}
          <NavLink to="/producer-packs" className={navLinkClass} data-testid="nav-producer-packs" onClick={handleNavClick}>
            <Music size={16} className="text-[#FFDA33]" /> Sample Packs
          </NavLink>
          
          {/* Videos — accessible to everyone */}
          <NavLink to="/videos" className={navLinkClass} data-testid="nav-videos" onClick={handleNavClick}>
            <Play size={16} className="text-[#ff6600]" /> Videos
          </NavLink>
          
          <NavLink to="/music-review" className={navLinkClass} data-testid="nav-music-review" onClick={handleNavClick}>
            <Mic size={16} className="text-[#fdcb6e]" /> Music Review
          </NavLink>
          
          <NavLink to="/social-kit" className={navLinkClass} data-testid="nav-social-kit" onClick={handleNavClick}>
            <Share2 size={16} className="text-[#e17055]" /> Social Media Kit
          </NavLink>
          
          <NavLink to="/events" className={navLinkClass} data-testid="nav-events" onClick={handleNavClick}>
            <Ticket size={16} className="text-[#fd79a8]" /> Events
          </NavLink>
          
          {isAuthenticated && (
            <NavLink to="/email-blast" className={navLinkClass} data-testid="nav-email-blast" onClick={handleNavClick}>
              <Megaphone size={16} className="text-[#e84393]" /> Email Blast
            </NavLink>
          )}
          
          <NavLink to="/gigs" className={navLinkClass} data-testid="nav-gigs" onClick={handleNavClick}>
            <Store size={16} className="text-[#6c5ce7]" /> Gig Market
          </NavLink>

          <NavLink to="/merch" className={navLinkClass} data-testid="nav-merch-shop" onClick={handleNavClick}>
            <ShoppingBag size={16} className="text-[#FFDA33]" /> Shop
          </NavLink>

          {/* Cart has moved inside /merch as a sub-tab — no separate sidebar link. */}

          {isAuthenticated ? (
            <NavLink to="/playlists" className={navLinkClass} data-testid="nav-playlists" onClick={handleNavClick}>
              <ListMusic size={16} className="text-[#55efc4]" /> Playlists
            </NavLink>
          ) : (
            <button 
              onClick={() => handleLoginRedirect('playlists')}
              className="flex items-center gap-2.5 px-3 py-2 text-xs font-mono uppercase tracking-wider text-[#888888] border-l-2 border-transparent hover:text-white hover:bg-[#0a0a0a] hover:border-[#333] w-full text-left"
            >
              <ListMusic size={16} className="text-[#55efc4]" /> Playlists
            </button>
          )}
          
          {isCreator && (
            <NavLink to="/collaborations" className={navLinkClass} data-testid="nav-collabs" onClick={handleNavClick}>
              <Users size={16} className="text-[#ffeaa7]" /> Collabs
            </NavLink>
          )}
          
          {isAuthenticated ? (
            <NavLink to="/communities" className={navLinkClass} data-testid="nav-communities" onClick={handleNavClick}>
              <Users size={16} className="text-[#81ecec]" /> Communities
            </NavLink>
          ) : (
            <button 
              onClick={() => handleLoginRedirect('communities')}
              className="flex items-center gap-2.5 px-3 py-2 text-xs font-mono uppercase tracking-wider text-[#888888] border-l-2 border-transparent hover:text-white hover:bg-[#0a0a0a] hover:border-[#333] w-full text-left"
            >
              <Users size={16} className="text-[#81ecec]" /> Communities
            </button>
          )}
          
          {isAuthenticated ? (
            <NavLink to="/messages" className={navLinkClass} data-testid="nav-messages" onClick={handleNavClick}>
              <MessageCircle size={16} className="text-[#74b9ff]" /> Messages
            </NavLink>
          ) : (
            <button 
              onClick={() => handleLoginRedirect('messages')}
              className="flex items-center gap-2.5 px-3 py-2 text-xs font-mono uppercase tracking-wider text-[#888888] border-l-2 border-transparent hover:text-white hover:bg-[#0a0a0a] hover:border-[#333] w-full text-left"
            >
              <MessageCircle size={16} className="text-[#74b9ff]" /> Messages
            </button>
          )}

          {/* DJ-specific public features */}
          {isDJ && (
            <>
              <NavLink to="/dj" className={navLinkClass} data-testid="nav-dj" onClick={handleNavClick}>
                <Disc size={16} className="text-[#00CCFF]" /> DJ Hub
              </NavLink>
              <NavLink to="/mood-playlists" className={navLinkClass} data-testid="nav-mood-playlists" onClick={handleNavClick}>
                <Music size={16} className="text-[#dfe6e9]" /> Mood Playlists
              </NavLink>
              <NavLink to="/live-sessions" className={navLinkClass} data-testid="nav-live-sessions" onClick={handleNavClick}>
                <Radio size={16} className="text-[#ff4757]" /> Live Sessions
              </NavLink>
            </>
          )}
          
          {(user?.is_dj || user?.is_promoter || isAdmin) && (
            <NavLink to="/invoices" className={navLinkClass} data-testid="nav-invoices" onClick={handleNavClick}>
              <FileText size={16} className="text-[#b2bec3]" /> Invoices
            </NavLink>
          )}

          {/* User-Owned Radio Stations — artists / AI artists / producers / DJs */}
          {(user?.is_artist || user?.is_ai_artist || user?.is_producer || user?.is_dj || isAdmin) && (
            <NavLink to="/radio-stations" className={navLinkClass} data-testid="nav-radio-stations" onClick={handleNavClick}>
              <Radio size={16} className="text-[#9b51e0]" /> Radio Stations
            </NavLink>
          )}
          
          <NavLink 
            to="/vip" 
            className={({ isActive }) => 
              `flex items-center gap-3 px-4 py-3 text-sm font-mono uppercase tracking-wider border-l-2 transition-all ${
                isActive 
                  ? 'text-[#00CCFF] bg-[#00CCFF]/10 border-[#00CCFF]' 
                  : isVIP 
                    ? 'text-[#00CCFF] border-transparent hover:text-white hover:bg-[#0a0a0a] hover:border-[#333]'
                    : 'text-[#888888] border-transparent hover:text-white hover:bg-[#0a0a0a] hover:border-[#333]'
              }`
            } 
            data-testid="nav-vip" 
            onClick={handleNavClick}
          >
            <Crown size={16} className={isVIP ? 'text-[#00CCFF]' : 'text-[#FFDA33]'} />
            VIP Lounge
            <Crown size={14} className="ml-auto text-[#FFDA33]" />
          </NavLink>

          {isCreatorProEligible && !isCreator && (
            <NavLink to="/creator-pro" className={navLinkClass} data-testid="nav-creator-pro-global" onClick={handleNavClick}>
              <Crown size={16} className="text-[#FFDA33]" />
              {hasCreatorPro ? 'Creator Pro · Active' : 'Get Creator Pro'}
            </NavLink>
          )}

          <NavLink to="/pro-plans" className={navLinkClass} data-testid="nav-pro-plans" onClick={handleNavClick}>
            <Crown size={16} className="text-[#FFDA33]" />
            Memberships &amp; Gifts
          </NavLink>
        </div>
        
        {/* ===== ROLE-SPECIFIC SECTIONS ===== */}

        {/* Artist / AI Artist / Producer — Dashboard, Upload, Earnings, Analytics
         * iter_429: section label + Dashboard label now respect all role
         * combinations (dual/trio). Previously a dual artist+producer
         * was labelled just "Creator" and "Dashboard" — non-descriptive
         * for users who want to see all their hats from the nav. */}
        {isAuthenticated && isCreator && (() => {
          let sectionLabel = 'Creator';
          let dashLabel = 'Dashboard';
          if (isAiArtist && isProducer && isArtist) {
            sectionLabel = 'Artist / AI / Producer';
            dashLabel = 'Creator Dashboard';
          } else if (isArtist && isProducer) {
            sectionLabel = 'Artist / Producer';
            dashLabel = 'Artist · Producer Dashboard';
          } else if (isArtist && isAiArtist) {
            sectionLabel = 'Artist / AI Artist';
            dashLabel = 'Artist · AI Dashboard';
          } else if (isAiArtist && isProducer) {
            sectionLabel = 'AI Artist / Producer';
            dashLabel = 'AI · Producer Dashboard';
          } else if (isAiArtist) {
            sectionLabel = 'AI Artist';
            dashLabel = 'AI Artist Dashboard';
          } else if (isProducer && !isArtist) {
            sectionLabel = 'Producer';
            dashLabel = 'Producer Dashboard';
          } else if (isArtist) {
            sectionLabel = 'Artist';
            dashLabel = 'Artist Dashboard';
          }
          return (
          <div className="mb-4">
            <p className="px-4 text-[10px] text-[#888888] uppercase tracking-widest mb-2">{sectionLabel}</p>
            <NavLink to="/dashboard" className={navLinkClass} data-testid="nav-dashboard" onClick={handleNavClick}>
              <LayoutDashboard size={16} className="text-[#00CCFF]" />
              {dashLabel}
            </NavLink>
            <NavLink to="/upload" className={navLinkClass} data-testid="nav-upload" onClick={handleNavClick}>
              <Upload size={16} className="text-[#00ff88]" /> Upload
            </NavLink>
            {isProducer && (
              <NavLink to="/upload?tab=beat" className={navLinkClass} data-testid="nav-beat-upload" onClick={handleNavClick}>
                <Music size={16} className="text-[#F59E0B]" /> Beat Upload
              </NavLink>
            )}
            <NavLink to="/earnings" className={navLinkClass} data-testid="nav-earnings" onClick={handleNavClick}>
              <DollarSign size={16} className="text-[#FFDA33]" /> Earnings
            </NavLink>
            {/* iter_483 — Royalties & Publishing hub */}
            <NavLink to="/royalties" className={navLinkClass} data-testid="nav-royalties" onClick={handleNavClick}>
              <Shield size={16} className="text-[#00CCFF]" /> Royalties &amp; Publishing
            </NavLink>
            {/* iter_485 — Fan-subscription drops feed */}
            <NavLink to="/fan-feed" className={navLinkClass} data-testid="nav-fan-feed" onClick={handleNavClick}>
              <DollarSign size={16} className="text-[#FFDA33]" /> Fan Drops
            </NavLink>
            {/* iter_486 — Public Listener Wall (always visible) */}
            <NavLink to="/listener-wall" className={navLinkClass} data-testid="nav-listener-wall" onClick={handleNavClick}>
              <MessageCircle size={16} className="text-[#ff4444]" /> Listener Wall
            </NavLink>
            <NavLink to="/creator-pro" className={navLinkClass} data-testid="nav-creator-pro" onClick={handleNavClick}>
              <Crown size={16} className="text-[#FFDA33]" />
              {hasCreatorPro ? 'Creator Pro · Active' : 'Get Creator Pro'}
            </NavLink>
            <NavLink to="/my-shop" className={navLinkClass} data-testid="nav-my-shop" onClick={handleNavClick}>
              <ShoppingBag size={16} className="text-[#FFDA33]" /> My Shop
            </NavLink>
            <NavLink to="/analytics" className={navLinkClass} data-testid="nav-analytics" onClick={handleNavClick}>
              <Activity size={16} className="text-[#ff6b6b]" /> Analytics
            </NavLink>
            <NavLink to="/mix-master/orders" className={navLinkClass} data-testid="nav-mix-master-orders" onClick={handleNavClick}>
              <Sliders size={16} className="text-[#a29bfe]" /> Mix &amp; Master
            </NavLink>
          </div>
          );
        })()}
        
        {/* DJ Section — Dashboard, Earnings */}
        {isAuthenticated && isDJ && (
          <div className="mb-4">
            <p className="px-4 text-[10px] text-[#00CCFF] uppercase tracking-widest mb-2">DJ</p>
            <NavLink to="/dj" className={navLinkClass} data-testid="nav-dj-dashboard" onClick={handleNavClick}>
              <LayoutDashboard size={16} className="text-[#00CCFF]" /> DJ Dashboard
            </NavLink>
            <NavLink to="/earnings" className={navLinkClass} data-testid="nav-dj-earnings" onClick={handleNavClick}>
              <DollarSign size={16} className="text-[#FFDA33]" /> Earnings
            </NavLink>
            <NavLink to="/revenue" className={navLinkClass} data-testid="nav-dj-revenue" onClick={handleNavClick}>
              <Wallet size={16} className="text-[#9333ea]" /> Revenue & Withdrawals
            </NavLink>
          </div>
        )}
        
        {/* Promoter Section — Dashboard, Earnings, Analytics (Events/Tickets) */}
        {isAuthenticated && isPromoter && (
          <div className="mb-4">
            <p className="px-4 text-[10px] text-[#ff6b6b] uppercase tracking-widest mb-2">Promoter</p>
            <NavLink to="/promoter" className={navLinkClass} data-testid="nav-promoter-dashboard" onClick={handleNavClick}>
              <LayoutDashboard size={16} className="text-[#e17055]" /> Promoter Dashboard
            </NavLink>
            <NavLink to="/earnings" className={navLinkClass} data-testid="nav-promoter-earnings" onClick={handleNavClick}>
              <DollarSign size={16} className="text-[#FFDA33]" /> Earnings
            </NavLink>
            <NavLink to="/revenue" className={navLinkClass} data-testid="nav-promoter-revenue" onClick={handleNavClick}>
              <Wallet size={16} className="text-[#9333ea]" /> Revenue & Withdrawals
            </NavLink>
            <NavLink to="/events" className={navLinkClass} data-testid="nav-promoter-events" onClick={handleNavClick}>
              <Ticket size={16} className="text-[#fd79a8]" /> My Events
            </NavLink>
            <NavLink to="/analytics" className={navLinkClass} data-testid="nav-promoter-analytics" onClick={handleNavClick}>
              <Activity size={16} className="text-[#ff6b6b]" /> Analytics
            </NavLink>
          </div>
        )}
        
        {/* Advertiser Section — Dashboard */}
        {isAuthenticated && (isAdvertiser || isAdmin) && (
          <div className="mb-4">
            <p className="px-4 text-[10px] text-[#00CCFF] uppercase tracking-widest mb-2">Advertiser</p>
            <NavLink to="/advertiser" className={navLinkClass} data-testid="nav-advertiser" onClick={handleNavClick}>
              <BarChart3 size={16} className="text-[#00CCFF]" /> Ad Campaigns
            </NavLink>
          </div>
        )}

        {/* Fan Section — View Profile */}
        {isAuthenticated && isFan && (
          <div className="mb-4">
            <p className="px-4 text-[10px] text-[#888888] uppercase tracking-widest mb-2">Account</p>
            <NavLink to={`/profile/${user?.id}`} className={navLinkClass} data-testid="nav-fan-profile" onClick={handleNavClick}>
              <User size={16} className="text-[#888888]" /> View Profile
            </NavLink>
          </div>
        )}
        
        {/* Reviewer Section — Priority Review dashboard */}
        {isAuthenticated && isReviewer && (
          <div className="mb-4">
            <p className="px-4 text-[10px] text-[#9b59b6] uppercase tracking-widest mb-2">Reviewer</p>
            <NavLink to="/reviewer" className={navLinkClass} data-testid="nav-reviewer-dashboard" onClick={handleNavClick}>
              <Headphones size={16} className="text-[#9b59b6]" /> Reviewer Dashboard
            </NavLink>
          </div>
        )}

        {/* Admin Section */}
        {isAdmin && (
          <div className="mb-4">
            <p className="px-4 text-[10px] text-[#FFDA33] uppercase tracking-widest mb-2">Admin</p>
            <NavLink to="/admin" className={navLinkClass} data-testid="nav-admin" onClick={handleNavClick}>
              <Shield size={16} className="text-[#FFDA33]" /> Admin Panel
            </NavLink>
          </div>
        )}

        {/* ===== ACCOUNT & MORE — surfaces that previously existed only
              as deep-link routes (My Subscriptions, Sync Catalog, Listening
              Parties, Event Map, etc.). Putting them in the sidebar so they
              are actually discoverable. Visible to every authenticated user.
        ===== */}
        {isAuthenticated && (
          <div className="mb-4">
            <p className="px-4 text-[10px] text-[#888888] uppercase tracking-widest mb-2">More</p>
            {isCreator && (
              <NavLink to="/royalties" className={navLinkClass} data-testid="nav-royalties-more" onClick={handleNavClick}>
                <Shield size={16} className="text-[#00CCFF]" /> Royalties &amp; Publishing
              </NavLink>
            )}
            <NavLink to="/my-subscriptions" className={navLinkClass} data-testid="nav-my-subscriptions" onClick={handleNavClick}>
              <Repeat size={16} className="text-[#00CCFF]" /> My Subscriptions
            </NavLink>
            <NavLink to="/sync-catalog" className={navLinkClass} data-testid="nav-sync-catalog" onClick={handleNavClick}>
              <Film size={16} className="text-[#FFDA33]" /> Sync Catalog
            </NavLink>
            <NavLink to="/listening-parties" className={navLinkClass} data-testid="nav-listening-parties" onClick={handleNavClick}>
              <PartyPopper size={16} className="text-[#ff6b6b]" /> Listening Parties
            </NavLink>
            <NavLink to="/event-map" className={navLinkClass} data-testid="nav-event-map" onClick={handleNavClick}>
              <MapPin size={16} className="text-[#00ff88]" /> Event Map
            </NavLink>
            <NavLink to="/marketplace" className={navLinkClass} data-testid="nav-marketplace" onClick={handleNavClick}>
              <Briefcase size={16} className="text-[#74b9ff]" /> Gig Marketplace
            </NavLink>
            <NavLink to="/paid-to-listen" className={navLinkClass} data-testid="nav-paid-to-listen" onClick={handleNavClick}>
              <Coins size={16} className="text-[#FFDA33]" /> Paid to Listen
            </NavLink>
            <NavLink to="/merch/wishlist" className={navLinkClass} data-testid="nav-wishlist" onClick={handleNavClick}>
              <Heart size={16} className="text-[#ff4757]" /> Wishlist
            </NavLink>
            <NavLink to="/cart" className={navLinkClass} data-testid="nav-cart" onClick={handleNavClick}>
              <ShoppingCart size={16} className="text-[#dfe6e9]" /> Cart
            </NavLink>
            <NavLink to="/invoices" className={navLinkClass} data-testid="nav-invoices-more" onClick={handleNavClick}>
              <FileText size={16} className="text-[#b2bec3]" /> Invoices
            </NavLink>
            <NavLink to="/fees" className={navLinkClass} data-testid="nav-fees" onClick={handleNavClick}>
              <CreditCard size={16} className="text-[#888888]" /> Fees &amp; Pricing
            </NavLink>
            <NavLink to="/help" className={navLinkClass} data-testid="nav-help" onClick={handleNavClick}>
              <MessageCircle size={16} className="text-[#888888]" /> Help &amp; FAQ
            </NavLink>
          </div>
        )}

        {/* Online Following */}
        {isAuthenticated && (
          <OnlineFollowing />
        )}
      </nav>

      {/* iter_407 — My Library moved to the top of the main nav so every
          signed-in user finds it immediately (was orphaned here at the
          bottom of the sidebar). The block below is intentionally kept
          empty / removed. */}

      {/* User Profile Footer */}
      <div className="border-t border-[#1a1a1a] p-4">
        {isAuthenticated ? (
          <>
            <NavLink 
              to={`/profile/${user?.id}`}
              className="flex items-center gap-3 mb-3 p-2 rounded hover:bg-[#111111] transition-colors"
              onClick={handleNavClick}
              data-testid="nav-profile"
            >
              <div className="w-10 h-10 bg-[#111111] border border-[#1a1a1a] overflow-hidden flex-shrink-0">
                {user?.profile_picture ? (
                  <img
                    src={getProfilePicUrl(user.profile_picture, user.id)}
                    alt={user.username}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User size={20} className="text-[#888888]" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-mono text-sm truncate">{user?.username}</p>
                <p className={`text-xs font-mono uppercase flex items-center gap-1 ${userRole.color}`}>
                  <RoleIcon size={12} /> {userRole.label}
                </p>
              </div>
            </NavLink>
            {/* Theme Toggle */}
            <div className="flex items-center justify-center gap-1 py-1.5 border-t border-[#1a1a1a]">
              <button
                onClick={toggleTheme}
                className={`p-1.5 rounded transition-colors ${mode === 'light' ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-800 text-zinc-500'}`}
                title={mode === 'light' ? 'Switch to dark' : 'Switch to light'}
                data-testid="theme-toggle"
              >
                {mode === 'light' ? <Sun size={13} /> : <Moon size={13} />}
              </button>
              <button
                onClick={enableAutoTheme}
                className={`p-1.5 rounded transition-colors ${autoMode ? 'bg-[#00CCFF]/20 text-[#00CCFF]' : 'bg-zinc-800 text-zinc-500'}`}
                title="Auto: Light by day, dark by night"
                data-testid="theme-auto"
              >
                <Clock size={13} />
              </button>
            </div>
            {/* Newsletter signup widget */}
            <NewsletterWidget />

            <div className="flex gap-2">
              <NavLink 
                to="/settings" 
                className="flex-1 py-2 px-3 text-center text-[#888888] hover:text-white hover:bg-[#111111] transition-colors text-xs font-mono uppercase"
                onClick={handleNavClick}
                data-testid="nav-settings"
              >
                <Settings size={14} className="mx-auto mb-1" /> Settings
              </NavLink>
              <button
                onClick={handleLogout}
                className="flex-1 py-2 px-3 text-center text-[#888888] hover:text-red-500 hover:bg-[#111111] transition-colors text-xs font-mono uppercase"
                data-testid="logout-btn"
              >
                <LogOut size={14} className="mx-auto mb-1" /> Logout
              </button>
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-2">
            <NavLink 
              to="/login" 
              className="flex items-center justify-center gap-2 py-2 px-4 bg-[#00CCFF] text-black font-mono text-sm uppercase hover:bg-[#00b8e6] transition-colors"
              onClick={handleNavClick}
              data-testid="nav-login"
            >
              <LogIn size={16} /> Login
            </NavLink>
            <NavLink 
              to="/signup" 
              className="flex items-center justify-center gap-2 py-2 px-4 border border-[#00CCFF] text-[#00CCFF] font-mono text-sm uppercase hover:bg-[#00CCFF]/10 transition-colors"
              onClick={handleNavClick}
              data-testid="nav-signup"
            >
              <UserPlus size={16} /> Sign Up
            </NavLink>
          </div>
        )}
      </div>
    </aside>
  );
};
